import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Sidebar from '../components/shared/Sidebar';
import useAuthStore from '../store/authStore';
import financialSubmissionService, { SubmittedFinancialData, SubmittedFinancialDataLite } from '../services/financialSubmissionService';
import organisationKPIsService, { OrganisationKPIs } from '../services/organisationKPIsService';
import organisationSettingsService, { OrganisationSettings } from '../services/organisationSettingsService';
import LocationGroupService from '../services/locationGroupService';
import KPIDetailsModal from '../components/analytics/KPIDetailsModal';
import QualityCheckResultModal from '../components/analytics/QualityCheckResultModal';
import FinancialDataChatbot from '../components/chat/FinancialDataChatbot';
import SubmissionFilterPanel, { SubmissionFilter } from '../components/data/SubmissionFilterPanel';
import './ViewSubmissionsPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const ViewSubmissionsPage: React.FC = () => {
  const { user } = useAuthStore();
  const [submissions, setSubmissions] = useState<SubmittedFinancialDataLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessibleLocations, setAccessibleLocations] = useState<any[]>([]);
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  const [organisationSettings, setOrganisationSettings] = useState<OrganisationSettings | null>(null);
  
  // Custom import settings from org
  const [customImportSettings, setCustomImportSettings] = useState<any>(null);
  const [customImportChecked, setCustomImportChecked] = useState(false);
  
  // Modal state
  const [selectedSubmission, setSelectedSubmission] = useState<SubmittedFinancialData | null>(null);
  const [selectedSubmissionLite, setSelectedSubmissionLite] = useState<SubmittedFinancialDataLite | null>(null);
  const [loadingSubmissionDetails, setLoadingSubmissionDetails] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [orgKPIRules, setOrgKPIRules] = useState<OrganisationKPIs[]>([]);
  
  // Quality check result modal state
  const [isQualityModalOpen, setIsQualityModalOpen] = useState(false);
  const [qualityCheckResult, setQualityCheckResult] = useState<{
    score: number;
    report: string;
    submissionId: number;
  } | null>(null);
  
  // AI Chatbot state
  const [showChatbot, setShowChatbot] = useState(false);
  
  // Location groups state for AI Data Assistant
  const [locationGroups, setLocationGroups] = useState<any[]>([]);
  const [locationGroupsLoaded, setLocationGroupsLoaded] = useState(false);

  // Filter state
  const [submissionFilter, setSubmissionFilter] = useState<SubmissionFilter>({
    locations: [],
    years: [],
    months: [],
    kpiCategories: [],
    includeAllQualityScores: false,
    intelligentFiltering: false
  });
  
  // Refs to prevent duplicate API calls
  const locationsLoadingRef = useRef(false);
  const submissionsLoadingRef = useRef(false);

  // Memoized function to fetch accessible locations
  const fetchAccessibleLocations = useCallback(async () => {
    if (!user || locationsLoadingRef.current || locationsLoaded) return;
    
    console.log('🔄 Fetching accessible locations...');
    locationsLoadingRef.current = true;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/user-access/accessible-locations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAccessibleLocations(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching accessible locations:', error);
      setAccessibleLocations([]);
    } finally {
      setLocationsLoaded(true);
      locationsLoadingRef.current = false;
    }
  }, [user, locationsLoaded]);

  // Fetch accessible locations for the user
  useEffect(() => {
    fetchAccessibleLocations();
  }, [fetchAccessibleLocations]);

  // Memoized function to fetch organization KPI rules
  const fetchOrgKPIRules = useCallback(async () => {
    if (!user) return;
    
    try {
      console.log('🔄 Fetching organization KPI rules...');
      const rules = await organisationKPIsService.getOrganisationKPIs();
      console.log('📋 Organization KPI rules received:', rules.length);
      setOrgKPIRules(rules);
    } catch (err) {
      console.error('❌ Error fetching organization KPI rules:', err);
      // Don't set error state for KPI rules as it's not critical for viewing submissions
    }
  }, [user]);

  // Memoized function to fetch organization settings
  const fetchOrganisationSettings = useCallback(async () => {
    if (!user) return;
    
    try {
      console.log('🔄 Fetching organisation settings...');
      const settings = await organisationSettingsService.getOrganisationSettings();
      console.log('⚙️ Organisation settings received:', settings);
      setOrganisationSettings(settings);
      
      // Also fetch org-level settings for customImport config
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/organizations/current`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const orgData = await response.json();
        if (orgData.success && orgData.data?.settings?.customImport) {
          console.log('📊 Custom import settings:', orgData.data.settings.customImport);
          setCustomImportSettings(orgData.data.settings.customImport);
        }
      }
      setCustomImportChecked(true);
    } catch (err) {
      console.error('❌ Error fetching organisation settings:', err);
      setCustomImportChecked(true); // Mark as checked even on error
      // Don't set error state for settings as it's not critical for viewing submissions
    }
  }, [user]);

  // Memoized function to fetch submissions
  // Uses lite endpoint (fast, no kpi_data) for large datasets
  // Fallback: always use lite for org 34 (Nissan) which has millions of KPI entries
  const fetchSubmissions = useCallback(async () => {
    if (!user || !locationsLoaded || !customImportChecked || submissionsLoadingRef.current) return;
    
    // Use lite mode if customImport is enabled OR if org_id is 34 (Nissan - large dataset fallback)
    const isNissanOrg = user.organization_id === 34;
    const useLiteMode = customImportSettings?.enabled === true || isNissanOrg;
    console.log('🔄 Fetching financial submissions...', useLiteMode ? `(LITE MODE - ${isNissanOrg ? 'Nissan org' : 'customImport enabled'})` : '(FULL MODE)');
    
    submissionsLoadingRef.current = true;
    setLoading(true);
    setError(null);
    
    try {
      // Use lite endpoint ONLY for custom import orgs (large datasets with period types)
      // Normal orgs use full endpoint since their kpi_data is manageable
      let data: any[];
      if (useLiteMode) {
        data = await financialSubmissionService.getFinancialSubmissionsLite();
      } else {
        const fullData = await financialSubmissionService.getFinancialSubmissions();
        // Convert full data to lite-compatible format for the list
        data = fullData.map(sub => ({
          ...sub,
          kpiCount: sub.kpiData?.length || 0,
          uniqueKpiCount: sub.kpiData?.length || 0,
          periodTypes: []
        }));
      }
      
      // Filter submissions based on accessible locations only
      // Quality filtering will be handled in the filteredSubmissions useMemo based on toggle state
      const locationFilteredSubmissions = data.filter(submission => {
        // If user has no location restrictions, show all submissions for their org
        if (!submission.locationId || accessibleLocations.length === 0) {
          return true;
        }
        
        // Check if user has access to this location
        return accessibleLocations.some(loc => loc.id === submission.locationId);
      });
      
      setSubmissions(locationFilteredSubmissions);
    } catch (err: any) {
      console.error('Error fetching submissions:', err);
      setError('Failed to load submitted financial data');
    } finally {
      setLoading(false);
      submissionsLoadingRef.current = false;
    }
  }, [user, locationsLoaded, accessibleLocations, customImportChecked, customImportSettings]);

  // Fetch organization KPI rules
  useEffect(() => {
    fetchOrgKPIRules();
  }, [fetchOrgKPIRules]);

  // Fetch organisation settings
  useEffect(() => {
    fetchOrganisationSettings();
  }, [fetchOrganisationSettings]);

  // Fetch submitted financial data
  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  // Fetch location groups for AI Data Assistant
  useEffect(() => {
    const fetchLocationGroups = async () => {
      if (!user?.organization_id) {
        console.log('🔍 ViewSubmissions: No organization_id, skipping location groups fetch');
        return;
      }
      
      if (locationGroupsLoaded) {
        console.log('🔍 ViewSubmissions: Location groups already loaded');
        return;
      }
      
      try {
        console.log('🏢 ViewSubmissions: Fetching location groups for AI Data Assistant...', { orgId: user.organization_id });
        const groups = await LocationGroupService.getGroupsByOrganization(user.organization_id);
        setLocationGroups(groups);
        setLocationGroupsLoaded(true);
        console.log(`🏢 ViewSubmissions: Loaded ${groups.length} location groups for AI Data Assistant`, groups);
      } catch (error) {
        console.error('❌ ViewSubmissions: Error fetching location groups:', error);
        setLocationGroupsLoaded(true); // Mark as loaded even on error to prevent retries
      }
    };
    
    fetchLocationGroups();
  }, [user?.organization_id, locationGroupsLoaded]);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const getLocationName = useCallback((locationId?: number) => {
    if (!locationId) return 'Organization Level';
    const location = accessibleLocations.find(loc => loc.id === locationId);
    return location ? location.name : `Location ${locationId}`;
  }, [accessibleLocations]);

  // Apply client-side filtering to submissions
  const filteredSubmissions = useMemo(() => {
    let filtered = submissions;

    // Apply quality score filter based on toggle state
    if (!submissionFilter.includeAllQualityScores) {
      // Only show submissions with quality scores EQUAL TO OR ABOVE the validation lower threshold
      filtered = filtered.filter(submission => {
        if (organisationSettings) {
          if (submission.dataQualityScore === null || submission.dataQualityScore === undefined) {
            return false; // Exclude submissions without quality scores
          }
          if (submission.dataQualityScore < organisationSettings.validationLower) {
            return false; // Exclude submissions below validation lower threshold
          }
        } else {
          // If no organisation settings, show all submissions with quality scores
          if (submission.dataQualityScore === null || submission.dataQualityScore === undefined) {
            return false;
          }
        }
        return true;
      });
    }
    // If includeAllQualityScores is true, show all submissions regardless of quality score

    // Apply location filter
    if (submissionFilter.locations.length > 0) {
      filtered = filtered.filter(submission => 
        submissionFilter.locations.includes(getLocationName(submission.locationId))
      );
    }

    // Apply year filter
    if (submissionFilter.years.length > 0) {
      filtered = filtered.filter(submission => 
        submissionFilter.years.includes(submission.year)
      );
    }

    // Apply month filter
    if (submissionFilter.months.length > 0) {
      filtered = filtered.filter(submission => 
        submissionFilter.months.includes(submission.month)
      );
    }

    return filtered;
  }, [submissions, submissionFilter, getLocationName, organisationSettings]);

  // Transform filtered submissions into format for AI chatbot, excluding quality reports
  const aiDataFilter = useMemo(() => {
    // Build location group map
    const locationGroupMap = new Map<number, string[]>();
    
    console.log(`🔍 ViewSubmissions AI Data Assistant: Building location group map from ${locationGroups.length} groups`);
    
    locationGroups.forEach(group => {
      console.log(`🔍 Group "${group.group_name}" has ${group.location_ids?.length || 0} locations:`, group.location_ids);
      group.location_ids?.forEach((locationId: number) => {
        if (!locationGroupMap.has(locationId)) {
          locationGroupMap.set(locationId, []);
        }
        locationGroupMap.get(locationId)!.push(group.group_name);
      });
    });
    
    console.log(`🏢 ViewSubmissions: Built location group map for AI Data Assistant with ${locationGroupMap.size} locations`);
    console.log(`🏢 LocationGroupMap contents:`, Array.from(locationGroupMap.entries()).slice(0, 5));
    
    // Get sample submission location IDs
    const sampleSubmissionLocationIds = filteredSubmissions.slice(0, 5).map(s => s.locationId);
    console.log(`🔍 Sample submission location IDs:`, sampleSubmissionLocationIds);
    
    const filter = {
      // Add explicit flag to indicate this is submission data
      isSubmissionData: true,
      // Extract unique values from filtered submissions
      years: Array.from(new Set(filteredSubmissions.map(s => s.year))),
      months: Array.from(new Set(filteredSubmissions.map(s => s.month))),
      locations: Array.from(new Set(filteredSubmissions.map(s => getLocationName(s.locationId)).filter(Boolean))),
      // Include filtered submission data (without quality reports) with location names AND location groups
      submissionData: filteredSubmissions.map((submission, idx) => {
        const { dataQualityReport, ...submissionWithoutReport } = submission;
        const locationGroupsForSubmission = submission.locationId 
          ? locationGroupMap.get(submission.locationId) || []
          : [];
        
        // Debug first 3 submissions
        if (idx < 3) {
          console.log(`🔍 Submission ${idx}: LocationID=${submission.locationId}, Groups=${JSON.stringify(locationGroupsForSubmission)}`);
        }
        
        return {
          ...submissionWithoutReport,
          locationName: getLocationName(submission.locationId), // Add readable location name
          locationGroups: locationGroupsForSubmission // Add location groups
        };
      }),
      limit: filteredSubmissions.length
    };
    
    console.log('🚀 Frontend aiDataFilter created:', {
      isSubmissionData: filter.isSubmissionData,
      submissionDataCount: filter.submissionData.length,
      sampleSubmission: filter.submissionData[0],
      years: filter.years,
      locations: filter.locations
    });
    
    return filter;
  }, [filteredSubmissions, getLocationName, locationGroups]);

  const getMonthName = useCallback((month: number) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[month - 1] || month.toString();
  }, []);

  // Modal handlers - fetch full details when viewing
  const handleViewSubmission = useCallback(async (submissionLite: SubmittedFinancialDataLite) => {
    if (!submissionLite.id) return;
    
    setSelectedSubmissionLite(submissionLite);
    setLoadingSubmissionDetails(true);
    setIsModalOpen(true);
    
    try {
      // Fetch full submission details with kpi_data
      const fullSubmission = await financialSubmissionService.getFinancialSubmissionById(submissionLite.id);
      if (fullSubmission) {
        setSelectedSubmission(fullSubmission);
      }
    } catch (err) {
      console.error('Error fetching submission details:', err);
    } finally {
      setLoadingSubmissionDetails(false);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedSubmission(null);
    setSelectedSubmissionLite(null);
  }, []);

  // Filter handlers
  const handleApplyFilter = useCallback((filter: SubmissionFilter) => {
    setSubmissionFilter(filter);
  }, []);

  const handleClearFilter = useCallback(() => {
    setSubmissionFilter({
      locations: [],
      years: [],
      months: [],
      kpiCategories: [],
      includeAllQualityScores: false,
      intelligentFiltering: false
    });
  }, []);

  return (
    <div className="dashboard-page">
      <Sidebar />
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="breadcrumbs">
            <span className="breadcrumb-current">View Submissions</span>
          </div>

          <div className="page-header">
            <h1>View Submissions</h1>
            <p className="page-description">
              View and manage all data submissions across your accessible locations
            </p>
          </div>



          {/* Submission Filter Panel */}
          <SubmissionFilterPanel
            submissions={submissions}
            onApplyFilter={handleApplyFilter}
            onClearFilter={handleClearFilter}
            getLocationName={getLocationName}
          />

          <div className="content-container">
            {!user ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading user data...</p>
              </div>
            ) : loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading submitted financial data...</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <div className="error-icon">⚠️</div>
                <h3>Error Loading Data</h3>
                <p>{error}</p>
                <button 
                  className="retry-button"
                  onClick={() => window.location.reload()}
                >
                  Retry
                </button>
              </div>
            ) : filteredSubmissions.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📊</div>
                <h3>No Submissions Found</h3>
                <p>No submitted financial data found for your accessible locations.</p>
              </div>
            ) : (
              <div className="submissions-table-container">
                <div className="table-header">
                  <h3>
                    {submissionFilter.locations.length > 0 || submissionFilter.years.length > 0 || submissionFilter.months.length > 0
                      ? `Filtered Submissions (${filteredSubmissions.length} of ${submissions.length})`
                      : `All Submissions (${filteredSubmissions.length})`
                    }
                  </h3>
                </div>
                
                <div className="submissions-table">
                  <div className="table-header-row">
                    <div className="table-cell header">Location</div>
                    <div className="table-cell header">Period</div>
                    <div className="table-cell header">KPIs</div>
                    <div className="table-cell header">Submitted</div>
                    <div className="table-cell header">Quality Score</div>
                    <div className="table-cell header">Actions</div>
                  </div>
                  
                  {filteredSubmissions.map((submission) => (
                    <div key={submission.id} className="table-row">
                      <div className="table-cell">
                        <div className="location-info">
                          <strong>{getLocationName(submission.locationId)}</strong>
                        </div>
                      </div>
                      
                      <div className="table-cell">
                        <div className="period-info">
                          <strong>{getMonthName(submission.month)} {submission.year}</strong>
                        </div>
                      </div>
                      
                      <div className="table-cell">
                        <div className="kpi-count">
                          <span className="count-badge">{submission.uniqueKpiCount || submission.kpiCount}</span>
                          <span className="count-label">KPIs</span>
                          {customImportSettings?.enabled && customImportSettings?.periodTypes && customImportSettings.periodTypes.length > 1 && (
                            <span className="period-types-badge" title={`Data types: ${customImportSettings.periodTypes.join(', ')}`}>
                              {customImportSettings.periodTypes.length} data types
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="table-cell">
                        <div className="submitted-info">
                          <div className="submitted-date">
                            {submission.createdAt ? formatDate(submission.createdAt) : 'Unknown'}
                          </div>
                        </div>
                      </div>
                      
                      <div className="table-cell">
                        <div className="quality-score">
                          {submission.dataQualityScore !== null && submission.dataQualityScore !== undefined ? (
                            <span className={`quality-badge ${submission.dataQualityScore >= 80 ? 'good' : submission.dataQualityScore >= 60 ? 'fair' : 'poor'}`}>
                              {submission.dataQualityScore}
                            </span>
                          ) : (
                            <span className="quality-badge pending">Pending</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="table-cell">
                        <div className="action-buttons">
                          <button 
                            className="view-button"
                            onClick={() => handleViewSubmission(submission)}
                          >
                            View
                          </button>
                          {submission.dataQualityScore !== null && submission.dataQualityScore !== undefined && (
                            <button 
                              className="quality-report-button"
                              onClick={() => {
                                setQualityCheckResult({
                                  score: submission.dataQualityScore!,
                                  report: submission.dataQualityReport || 'No quality report available.',
                                  submissionId: submission.id!
                                });
                                setIsQualityModalOpen(true);
                              }}
                            >
                              Quality Report
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KPI Details Modal */}
      {isModalOpen && (selectedSubmission || selectedSubmissionLite) && (
        <KPIDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          kpiData={selectedSubmission?.kpiData || []}
          loading={loadingSubmissionDetails}
          customImportSettings={customImportSettings}
          submissionInfo={{
            location: getLocationName(selectedSubmissionLite?.locationId || selectedSubmission?.locationId),
            period: `${getMonthName(selectedSubmissionLite?.month || selectedSubmission?.month || 1)} ${selectedSubmissionLite?.year || selectedSubmission?.year}`,
            submittedDate: formatDate(selectedSubmissionLite?.createdAt || selectedSubmission?.createdAt || ''),
            periodTypes: customImportSettings?.periodTypes || []
          }}
        />
      )}

      {/* Quality Check Result Modal */}
      {qualityCheckResult && (
        <QualityCheckResultModal
          isOpen={isQualityModalOpen}
          onClose={() => {
            setIsQualityModalOpen(false);
            setQualityCheckResult(null);
          }}
          qualityScore={qualityCheckResult.score}
          qualityReport={qualityCheckResult.report}
          submissionId={qualityCheckResult.submissionId}
        />
      )}

      {/* Chatbot Toggle Button - Outside content container for proper fixed positioning */}
      <div className="chatbot-toggle-container">
        <button 
          className="chatbot-toggle-button"
          onClick={() => setShowChatbot(!showChatbot)}
          title={showChatbot ? 'Close AI Assistant' : 'Open AI Assistant'}
        >
          <span className="chatbot-toggle-icon">💬</span>
          <span className="chatbot-toggle-text">
            {showChatbot ? 'Close AI Assistant' : 'Ask AI About Submissions'}
          </span>
          <span className="chatbot-toggle-count">
            {filteredSubmissions.length} submissions
          </span>
        </button>
      </div>

      {/* AI Data Assistant Chatbot */}
      <FinancialDataChatbot
        currentFilter={aiDataFilter}
        currentDataCount={filteredSubmissions.length}
        isVisible={showChatbot}
        onClose={() => setShowChatbot(false)}
      />
    </div>
  );
};

export default ViewSubmissionsPage;
